/* eslint-disable no-magic-numbers */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  DateCell,
  formatNumber,
  Icon,
  Inline,
  Tooltip,
  Typography,
} from '@omnipresentgroup/design-system';
import { isLiabilityShieldActiveInContract } from '@omnipresentgroup/domains';
import { toSentenceCase } from '@omnipresentgroup/helpers';
import type { Contractor } from '@omnipresentgroup/types';
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';

import { ContractorStatus } from '../../contractor-status';

const OMNIPROTECT_TOOLTIP_DESCRIPTION =
  'Contractor has Omniprotect: Shielding of legal liability from contractor misclassification';

export enum ColumnHeaders {
  contractorName = 'Contractor',
  liabilityShield = '',
  jobRole = 'Role',
  country = 'Country',
  startDate = 'Start date',
  endDate = 'End date',
  pay = 'Pay',
  state = 'Status',
}

export const useContractorsTableConfig = ({
  columnsOverride,
}: {
  columnsOverride?: ColumnDef<Contractor, unknown>[];
}) => {
  const columnHelper = useMemo(() => createColumnHelper<Contractor>(), []);

  const columns: ColumnDef<Contractor, any>[] = useMemo(
    () => [
      columnHelper.display({
        id: 'contractorName',
        cell: (info) =>
          `${info.row.original.firstName} ${info.row.original.lastName}`,
        header: () => ColumnHeaders.contractorName,
        size: 200,
        enableGlobalFilter: true,
      }),
      columnHelper.display({
        id: 'liabilityShield',
        cell: (info) => {
          return isLiabilityShieldActiveInContract(
            info.row.original.contracts[0],
          ) ? (
            <Inline align="center">
              <Tooltip
                id={`omniprotect-tooltip-${info.row.id}`}
                description={OMNIPROTECT_TOOLTIP_DESCRIPTION}
              >
                <Icon icon="World" size="16" />
              </Tooltip>
            </Inline>
          ) : undefined;
        },
        size: 10,
      }),
      columnHelper.display({
        id: 'jobRole',
        cell: (info) => info.row.original.contracts[0].jobRole,
        header: () => ColumnHeaders.jobRole,
        size: 100,
      }),
      columnHelper.display({
        id: 'country',
        cell: (info) => info.row.original.residenceCountry.name || '',
        header: () => ColumnHeaders.country,
        size: 100,
      }),
      columnHelper.display({
        id: 'startDate',
        cell: (info) => (
          <DateCell value={info.row.original.contracts[0].startDate} />
        ),
        sortingFn: 'datetime',
        header: () => ColumnHeaders.startDate,
        size: 100,
      }),
      columnHelper.display({
        id: 'endDate',
        cell: (info) => (
          <DateCell value={info.row.original.contracts[0].endDate} />
        ),
        sortingFn: 'datetime',
        header: () => ColumnHeaders.endDate,
        size: 100,
      }),
      columnHelper.display({
        id: 'pay',
        cell: (info) => {
          const amount = info.row.original.contracts[0].rate;
          if (amount) {
            const frequency =
              info.row.original.contracts[0].expectedInvoicingFrequency;
            return (
              <Inline gap="8" css={{ flexWrap: 'nowrap' }}>
                <Typography as="span" size="14" whiteSpace="nowrap">
                  {formatNumber({
                    input: Number(amount),
                    currency: info.row.original.contracts[0].invoicingCurrency,
                  })}
                </Typography>
                {frequency && (
                  <Typography
                    as="span"
                    size="14"
                    color="subtle"
                    whiteSpace="nowrap"
                  >
                    {toSentenceCase(frequency)}
                  </Typography>
                )}
              </Inline>
            );
          }
          return '';
        },
        header: () => ColumnHeaders.pay,
        size: 125,
      }),
      columnHelper.accessor('state', {
        cell: (info) => <ContractorStatus status={info.getValue()} />,
        header: () => ColumnHeaders.state,
        size: 75,
      }),
      ...(columnsOverride ?? []),
    ],
    [columnHelper, columnsOverride],
  );

  return {
    columns,
    columnHelper,
  };
};
